import { useState } from "react";
import { calendarService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { ITenantHolidayResponse } from "@miplatform/types";
import { createUUID } from "@miplatform/utils";

const getTenantHolidayUUID = createUUID();

export const useTenantHoliday = () => {
  const stateManager = useComponentState();

  const [tenantHoliday, setTenantHoliday] =
    useState<ITenantHolidayResponse | null>(null);
  const [refresh, setRefresh] = useState<boolean>(false);

  const getTenantHoliday = (id: string) => {
    stateManager.abort(getTenantHolidayUUID);
    stateManager.run(async () => {
      const response = await calendarService.tenantHolidays.get(id);

      if (response.status === HTTP_STATUSES.OK) {
        setTenantHoliday(response.data);
      }

      return response;
    });
  };

  const createTenantHoliday = (data: Partial<ITenantHolidayResponse>) => {
    return stateManager.run(async () => {
      const response = await calendarService.tenantHolidays.create({
        data,
        toastMessageConfig: {
          success: {
            text: `${data.name} created successfully.`,
          },
          failure: {
            text: `Failed to create ${data.name}. Please try again.`,
          },
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        setTenantHoliday(response.data);
        setRefresh((prev) => !prev);
      }

      return response;
    });
  };

  const updateTenantHoliday = (
    id: string,
    data: Partial<ITenantHolidayResponse>
  ) => {
    return stateManager.run(async () => {
      const response = await calendarService.tenantHolidays.update({
        id,
        data: {
          ...data,
          modifiedOn: data.modifiedOn ?? tenantHoliday?.modifiedOn,
        },

        toastMessageConfig: {
          success: {
            text: `${data.name} updated successfully.`,
          },
          failure: {
            text: `Failed to update ${data.name}. Please try again.`,
          },
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        setTenantHoliday(response.data);
        setRefresh((prev) => !prev);
      }

      return response;
    });
  };

  const deleteTenantHoliday = (holiday: ITenantHolidayResponse) => {
    return stateManager.run(async () => {
      const response = await calendarService.tenantHolidays.delete({
        data: holiday,
        toastMessageConfig: {
          success: {
            text: `${holiday.name} deleted successfully.`,
          },
          failure: {
            text: `Failed to delete ${holiday.name}. Please try again.`,
          },
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        setTenantHoliday(null);
        setRefresh((prev) => !prev);
      }

      return response;
    });
  };

  return {
    tenantHoliday,
    loading: stateManager.isBusy(),
    getTenantHoliday,
    createTenantHoliday,
    updateTenantHoliday,
    deleteTenantHoliday,
    refresh,
    setRefresh,
  };
};
